import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { skippedStepsNotice } from '@/lib/lesson-error';
import { notify } from '@/lib/notify';
import { Folder, X } from 'lucide-react';
import { LearningPrompt } from '@/components/features/home/LearningPrompt';
import { SuggestedTopics } from '@/components/features/home/SuggestedTopics';
import {
  trackGenerationRejected,
  trackGenerationRequested,
} from '@/components/features/home/trackGeneration';
import { TopicScopingDialog } from '@/components/features/home/TopicScopingDialog';
import { useSuggestedTopics } from '@/hooks/useDataProvider';
import {
  lessonApi,
  folderApi,
  type LessonSettings,
  type TopicSuggestion,
} from '@/lib/api-client';
import { useUserLessonSettings } from '@/hooks/account/useUserLessonSettings';
import { useEntitlementsUsage } from '@/hooks/account/useBilling';
import type { LearningTopic } from '@/types/learning-data';

interface LearningInterfaceProps {
  className?: string;
}

export function LearningInterface({ className = '' }: LearningInterfaceProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const targetFolderId = searchParams.get('folder');
  // Deep-dive promotion linkage - when present, the lesson that gets
  // created will be auto-attached to the source lesson's Extras and
  // the source chat message will be marked promoted. Both flags must
  // appear together; we hold them in component state so they survive
  // URL rewrites (e.g. clearing `topic` after pre-fill).
  const [deepDiveLinkage, setDeepDiveLinkage] = useState<{
    from_deep_dive_lesson: string;
    from_deep_dive_message: string;
  } | null>(null);
  const [query, setQuery] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  // Synchronous twin of isGenerating: two submits in the same tick (Enter
  // twice, click + Enter) both run before the state update re-renders, so
  // only a ref can make the second one a no-op.
  const generatingRef = useRef(false);
  // Inline safety-filter notice. Unlike a transient toast, this stays put and
  // is edit-oriented: a guardrail rejection won't clear until the user changes
  // the prompt, so we keep it visible next to the input they need to edit.
  const [policyNotice, setPolicyNotice] = useState<string | null>(null);

  // Read pre-fill params on mount: `topic` populates the prompt input,
  // `from_deep_dive_*` capture the source linkage. We clear the params
  // from the URL after consuming so a refresh doesn't keep re-applying
  // them.
  useEffect(() => {
    const topicParam = searchParams.get('topic');
    const fromLesson = searchParams.get('from_deep_dive_lesson');
    const fromMessage = searchParams.get('from_deep_dive_message');
    let mutated = false;
    const next = new URLSearchParams(searchParams);
    if (topicParam) {
      setQuery(topicParam);
      next.delete('topic');
      mutated = true;
    }
    if (fromLesson && fromMessage) {
      setDeepDiveLinkage({
        from_deep_dive_lesson: fromLesson,
        from_deep_dive_message: fromMessage,
      });
      next.delete('from_deep_dive_lesson');
      next.delete('from_deep_dive_message');
      mutated = true;
    }
    if (mutated) {
      setSearchParams(next, { replace: true });
    }
    // Intentionally a one-shot mount effect. Re-reading on every change
    // would fight the consumer setter calls above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch folder metadata (for banner label) when a target folder is set.
  const { data: targetFolder } = useQuery({
    queryKey: ['folders', 'detail', targetFolderId],
    queryFn: () => folderApi.getFolder(targetFolderId!),
    enabled: !!targetFolderId,
    staleTime: 60000,
    retry: false,
  });

  const clearTargetFolder = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('folder');
    setSearchParams(next, { replace: true });
  };

  // Topic scoping state
  const [showScopingDialog, setShowScopingDialog] = useState(false);
  const [scopingTopics, setScopingTopics] = useState<TopicSuggestion[]>([]);
  const [scopingChunks, setScopingChunks] = useState(0);
  const [isScopingLoading, setIsScopingLoading] = useState(false);
  const [pendingSourceIds, setPendingSourceIds] = useState<string[]>([]);
  const [pendingQuery, setPendingQuery] = useState('');
  const [pendingSettings, setPendingSettings] = useState<
    LessonSettings | undefined
  >(undefined);

  // Per-user lesson defaults - feeds the FlowSelector's initial state and
  // surfaces the user's saved custom flows in the dropdown. Stale-while-
  // revalidate is fine here; if the request fails (auth not ready, etc.)
  // FlowSelector falls back to `{ flow: 'core' }` and the home page still
  // works without account preferences.
  const { data: userLessonSettings } = useUserLessonSettings({
    staleTime: 5 * 60_000,
    retry: false,
  });

  // Fetch data based on current data mode (empty/mock/api).
  //
  // `data` is undefined until the query resolves (and on error).
  // `SuggestedTopics` already returns null for an empty array, so `[]` is the
  // correct loading state - it renders nothing, then the real list swaps in.
  const { data: suggestedTopics = [] } = useSuggestedTopics(6);
  // The plan's sources-per-lesson cap, so the composer refuses an extra
  // source up front instead of after the whole upload.
  const { data: entitlements } = useEntitlementsUsage();

  const generateLesson = async (
    topic: string,
    sourceIds?: string[],
    focusTopic?: string,
    files?: File[],
    settings?: LessonSettings,
    links?: import('@/lib/api-client').StagedLink[],
    texts?: import('@/lib/api-client').StagedText[]
  ) => {
    if (generatingRef.current) return;
    generatingRef.current = true;
    setIsGenerating(true);
    setPolicyNotice(null);
    try {
      const response = await lessonApi.generateLesson({
        topic,
        source_ids: sourceIds,
        focus_topic: focusTopic || undefined,
        folder_id: targetFolderId || undefined,
        files,
        links,
        texts,
        settings,
        // Forward deep-dive linkage if this generation was kicked off
        // from a "Make a lesson" CTA in another lesson's Extras.
        ...(deepDiveLinkage ?? {}),
      });
      trackGenerationRequested({
        lessonId: response.lesson_id,
        settings,
        sourceIds,
        files,
        links,
        texts,
        focusTopic,
        isDeepDive: deepDiveLinkage !== null,
      });
      const skipped = skippedStepsNotice(response.skipped_steps);
      if (skipped) notify.info(skipped);
      navigate(`/my-learning/${response.lesson_id}`);
    } catch (err: any) {
      console.error('❌ Failed to generate lesson:', err);
      trackGenerationRejected(err);
      // A safety-filter rejection (guardrail_rejected) needs a prompt change,
      // not a retry - surface it inline next to the input (which we leave
      // untouched) instead of a toast that disappears. Everything else stays
      // a toast.
      if (
        err?.status === 400 &&
        err?.data?.type === 'guardrail_rejected' &&
        err?.data?.reason !== 'moderation_unavailable'
      ) {
        setPolicyNotice(
          err?.data?.message ||
            "We can't create a lesson on this. Try rephrasing or a different topic."
        );
      } else {
        notify.error(
          err?.data?.message ||
            err.message ||
            'Failed to generate lesson. Please try again.'
        );
      }
      setIsGenerating(false);
    } finally {
      generatingRef.current = false;
    }
  };

  const handleLearningSubmit = async (
    query: string,
    files?: File[],
    options?: {
      settings?: LessonSettings;
      links?: import('@/lib/api-client').StagedLink[];
      texts?: import('@/lib/api-client').StagedText[];
    },
    sourceIds?: string[]
  ) => {
    if (!query.trim()) {
      notify.error('Please enter a topic to learn');
      return;
    }

    const trimmedQuery = query.trim();
    const hasInline =
      !!files?.length || !!options?.links?.length || !!options?.texts?.length;

    // Phase 1: home inline ingestion (files/links/texts) skips the scoping
    // dialog - bytes need to be processed before assess_sources can compute
    // chunk_count. Pre-existing source_ids still flow through assess+discover.
    if (sourceIds?.length && !hasInline) {
      try {
        const assessment = await lessonApi.assessSources(sourceIds);

        if (assessment.scoping_required) {
          // Large source - discover topics for user selection
          setPendingSourceIds(sourceIds);
          setPendingQuery(trimmedQuery);
          setPendingSettings(options?.settings);
          setIsScopingLoading(true);
          setShowScopingDialog(true);
          setScopingChunks(assessment.total_chunks);

          const topicsResult = await lessonApi.discoverTopics(sourceIds);
          setScopingTopics(topicsResult.topics);
          setIsScopingLoading(false);
          return;
        }
      } catch (err) {
        // Assessment is best-effort: if it fails the user can still
        // generate, just without the focus-topic picker for large
        // sources. Surface a quiet info toast so the user understands
        // why the scoping dialog didn't appear, instead of silently
        // dropping the feature.
        console.warn(
          'Source assessment failed, proceeding without scoping:',
          err
        );
        notify.info('Skipped source analysis - generating with defaults.');
      }
    }

    // No scoping needed - generate directly (with inline sources if any)
    await generateLesson(
      trimmedQuery,
      sourceIds,
      undefined,
      files,
      options?.settings,
      options?.links,
      options?.texts
    );
  };

  const handleTopicSelected = async (focusTopic: string | null) => {
    setShowScopingDialog(false);
    await generateLesson(
      pendingQuery,
      pendingSourceIds,
      focusTopic || undefined,
      undefined,
      pendingSettings
    );
  };

  const handleTopicSelect = (topic: LearningTopic) => {
    setQuery(topic.detailedPrompt);
  };

  return (
    <div className={`w-full space-y-8 sm:space-y-12 ${className}`}>
      {/* Main Learning Prompt + folder-context banner */}
      <div>
        {targetFolderId && (
          <div className='mx-auto mb-3 flex max-w-3xl items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-900/20 dark:text-amber-200'>
            <span className='flex items-center gap-2'>
              <Folder className='h-4 w-4' />
              <span>
                Creating in: <strong>{targetFolder?.name ?? 'folder'}</strong>
              </span>
            </span>
            <button
              type='button'
              onClick={clearTargetFolder}
              className='rounded-sm p-1 text-amber-700 transition-colors hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/40'
              aria-label='Create at root instead'
              title='Create at root instead'
            >
              <X className='h-3.5 w-3.5' />
            </button>
          </div>
        )}
        <LearningPrompt
          onSubmit={handleLearningSubmit}
          value={query}
          onChange={value => {
            setQuery(value);
            // Editing the prompt dismisses a stale safety-filter notice.
            if (policyNotice) setPolicyNotice(null);
          }}
          isLoading={isGenerating}
          initialSettings={userLessonSettings?.default_lesson_settings}
          savedFlows={userLessonSettings?.saved_custom_flows}
          clearOnSubmit={false}
          sourcesPerLesson={entitlements?.sources_per_lesson}
        />
        {policyNotice && (
          <p
            role='alert'
            className='mx-auto mt-2 max-w-4xl text-center text-sm text-amber-700 dark:text-amber-300'
          >
            {policyNotice}
          </p>
        )}
      </div>

      {/* Popular topics, for first-time and returning users alike.
       *
       * This used to fork on `isFirstTime`: newcomers got the topic chips and
       * returning users got a "Recent searches" list of two cards plus a
       * three-chip rump of the same topics further down. The list was the
       * weakest thing on the page - two full-width cards restating queries the
       * user could retype in less time than it took to read them - and the
       * split meant the chips were only ever seen at full strength by someone
       * on their first visit. One surface, one width, same six topics. */}
      <div>
        <SuggestedTopics
          topics={suggestedTopics}
          onTopicSelect={handleTopicSelect}
        />
      </div>

      {/* Topic Scoping Dialog for large sources */}
      <TopicScopingDialog
        isOpen={showScopingDialog}
        onClose={() => {
          setShowScopingDialog(false);
          setIsGenerating(false);
        }}
        onSelectTopic={handleTopicSelected}
        topics={scopingTopics}
        totalChunks={scopingChunks}
        isLoading={isScopingLoading}
      />
    </div>
  );
}
